import {
  get as restGet,
  post as restPost,
  put as restPut,
  del as restDelete,
  head as restHead,
  patch as restPatch,
} from 'aws-amplify/api';
import { getAuthSession } from './authUtilities';

async function resolveResponse(operation, { returnFullResponse = false } = {}) {
  const { response } = operation;
  const restResponse = await response;
  if (returnFullResponse) {
    return restResponse;
  }
  const { body } = restResponse;
  if (!body) {
    return undefined;
  }
  if (typeof body.json === 'function') {
    try {
      return await body.json();
    } catch (error) {
      if (typeof body.text === 'function') {
        const text = await body.text();
        try {
          return text ? JSON.parse(text) : undefined;
        } catch {
          return text;
        }
      }
      throw error;
    }
  }
  if (typeof body.text === 'function') {
    const text = await body.text();
    try {
      return text ? JSON.parse(text) : undefined;
    } catch {
      return text;
    }
  }
  return undefined;
}

function buildOptions(init = {}) {
  const {
    headers,
    body,
    queryStringParameters,
    response,
    withCredentials,
    abortController,
    ...rest
  } = init;

  return {
    operationOptions: {
      headers,
      body,
      queryParams: queryStringParameters,
      withCredentials,
      abortSignal: abortController?.signal,
      ...rest,
    },
    returnFullResponse: response === true,
  };
}

export async function apiGet(apiName, path, init = {}) {
  const { operationOptions, returnFullResponse } = buildOptions(init);
  const operation = restGet({ apiName, path, options: operationOptions });
  return resolveResponse(operation, { returnFullResponse });
}

export async function apiPost(apiName, path, init = {}) {
  const { operationOptions, returnFullResponse } = buildOptions(init);
  const operation = restPost({ apiName, path, options: operationOptions });
  return resolveResponse(operation, { returnFullResponse });
}

export async function apiPut(apiName, path, init = {}) {
  const { operationOptions, returnFullResponse } = buildOptions(init);
  const operation = restPut({ apiName, path, options: operationOptions });
  return resolveResponse(operation, { returnFullResponse });
}

export async function apiDelete(apiName, path, init = {}) {
  const { operationOptions, returnFullResponse } = buildOptions(init);
  const operation = restDelete({ apiName, path, options: operationOptions });
  return resolveResponse(operation, { returnFullResponse });
}

export async function apiHead(apiName, path, init = {}) {
  const { operationOptions, returnFullResponse } = buildOptions(init);
  const operation = restHead({ apiName, path, options: operationOptions });
  return resolveResponse(operation, { returnFullResponse });
}

export async function apiPatch(apiName, path, init = {}) {
  const { operationOptions, returnFullResponse } = buildOptions(init);
  const operation = restPatch({ apiName, path, options: operationOptions });
  return resolveResponse(operation, { returnFullResponse });
}

async function withAuthorization(init = {}) {
  const session = await getAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) {
    throw new Error('Missing authentication token');
  }
  return {
    ...init,
    headers: {
      ...(init.headers || {}),
      Authorization: token,
    },
  };
}

export async function apiGetAuth(apiName, path, init = {}) {
  return apiGet(apiName, path, await withAuthorization(init));
}

export async function apiPostAuth(apiName, path, init = {}) {
  return apiPost(apiName, path, await withAuthorization(init));
}

export async function apiPutAuth(apiName, path, init = {}) {
  return apiPut(apiName, path, await withAuthorization(init));
}

export async function apiDeleteAuth(apiName, path, init = {}) {
  return apiDelete(apiName, path, await withAuthorization(init));
}

export async function apiPatchAuth(apiName, path, init = {}) {
  return apiPatch(apiName, path, await withAuthorization(init));
}
